import React, { useState, useEffect, useCallback } from "react";
import { useCountryByName } from "../../hooks/useCountry";
import { useCheckAttendeeDuplicateEmailNationalIdAPONumber } from "../../hooks/useAttendee";
import doctorMasterDataService from "../../services/doctorMasterDataService";
import { toast } from "react-toastify";

function RegisterForm({
  values,
  setValues,
  setCurrentStep,
  setOriginalDoctorData,
  eventData,
}) {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const { data: cityData } = useCity();
  // const { data: saudiArabiaCities } = useCitiesByCountryName("Saudi Arabia");
  // const { data: currentCountryCities } = useCountryCities(values.country_id);
  const { data: saudiArabia } = useCountryByName("Saudi Arabia");
  const { mutateAsync: checkDuplicate } =
    useCheckAttendeeDuplicateEmailNationalIdAPONumber();

  // Set default country to Saudi Arabia when component mounts and user is new
  useEffect(() => {
    if (saudiArabia && !values.country_id && !values.national_id) {
      setValues((prev) => ({
        ...prev,
        country_id: saudiArabia.id,
      }));
    }
  }, [saudiArabia, values.country_id, values.national_id, setValues]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    // When city is selected, automatically set the country_id
    // if (name === "city_id") {
    //   const selectedCity = cityData?.find((city) => city.id === value);
    //   if (selectedCity) {
    //     setValues((prev) => ({
    //       ...prev,
    //       country_id: selectedCity.country_id || "",
    //     }));
    //   }
    // }

    if (name === "national_id") {
      const foundDoctor = await doctorMasterDataService.findDoctor(
        "national_id",
        value.trim()
      );
      if (foundDoctor) {
        setOriginalDoctorData(foundDoctor);
        setValues((prev) => ({
          ...prev,
          first_name: foundDoctor.first_name || "",
          family_name: foundDoctor.family_name || "",
          email: foundDoctor.email || "",
          phone_number: foundDoctor.phone_number || "",
          country_id: foundDoctor.country_id || "",
          // city_id: foundDoctor.city_id || "",
          organization: foundDoctor.organization || "",
          specialty: foundDoctor.specialty || "",
          APO_number: foundDoctor.APO_number || "",
          medical_license: foundDoctor.medical_license || "",
          doctor_master_data_id: foundDoctor.id || "",
          attendee_type: foundDoctor.attendee_type || "",
        }));

        if (foundDoctor.email.includes("@abbott.com")) {
          setValues((prev) => ({
            ...prev,
            attendee_type: "user",
          }));
        } else {
          setValues((prev) => ({
            ...prev,
            attendee_type: "normal_attendee",
          }));
        }
      } else {
        setOriginalDoctorData(null);
        setValues((prev) => ({
          ...prev,
          first_name: "",
          family_name: "",
          email: "",
          phone_number: "",
          country_id: saudiArabia?.id || "",
          // city_id: "",
          organization: "",
          specialty: "",
          APO_number: "",
          medical_license: "",
          doctor_master_data_id: "",
          attendee_type: "",
        }));
      }
    }
  };

  const validationRules = {
    national_id: {
      required: "National ID is required",
      pattern: "National ID must be a number",
    },
    first_name: {
      required: "First Name is required",
      minLength: "First Name must be at least 3 characters",
    },
    family_name: {
      required: "Family Name is required",
      minLength: "Family Name must be at least 3 characters",
    },
    email: {
      required: "Email is required",
      pattern: "Email must be a valid email address",
    },
    phone_number: {
      required: (formData) => {
        // Phone number is not required if organization is "Abbott"
        return formData.organization?.toLowerCase() === "abbott"
          ? false
          : "Phone Number is required";
      },
      pattern: "Phone Number must be a number",
    },
    specialty: { required: "Specialty is required" },
    medical_license: { required: "Medical License is required" },
    industry_area: { required: "Industry Area is required" },
  };

  const validateForm = (formData) => {
    const newErrors = {};
    for (const field in validationRules) {
      const value = formData[field] || "";
      const rules = validationRules[field];

      // Handle conditional required validation for phone_number
      if (field === "phone_number" && typeof rules.required === "function") {
        const isRequired = rules.required(formData);
        if (isRequired && !value.trim()) {
          newErrors[field] = isRequired;
          continue;
        }
        // If not required, skip pattern validation for empty values
        if (!isRequired && !value.trim()) {
          continue;
        }
      } else if (rules.required && !value.trim()) {
        newErrors[field] = rules.required;
        continue;
      }

      if (
        rules.minLength &&
        value.length < parseInt(rules.minLength.match(/\d+/)?.[0])
      ) {
        newErrors[field] = rules.minLength;
        continue;
      }

      if (rules.pattern && value.trim()) {
        let regex;
        if (field === "email") regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (field === "national_id") regex = /^\d+$/;
        if (field === "phone_number") regex = /^\d+$/;
        if (regex && !regex.test(value)) {
          newErrors[field] = rules.pattern;
        }
      }
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setIsSubmitting(true);

      try {
        const validationErrors = validateForm(values);
        if (Object.keys(validationErrors).length > 0) {
          toast.error("Please fill all required fields correctly");
          setIsSubmitting(false);
          return;
        }

        const duplicateCheck = await checkDuplicate({
          eventId: values.event_id,
          email: values.email.trim(),
          national_id: values.national_id.trim(),
        });

        if (duplicateCheck?.duplicateEmail) {
          toast.error(`Email ${values.email} already exists in this event`);
          setIsSubmitting(false);
          return;
        }
        if (duplicateCheck?.duplicateNationalId) {
          toast.error(
            `National ID ${values.national_id} already exists in this event`
          );
          setIsSubmitting(false);
          return;
        }

        setCurrentStep((prevActiveStep) => prevActiveStep + 1);
      } catch (error) {
        console.error("Registration error:", error);
        toast.error("Failed to process attendee data. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [values, checkDuplicate, setCurrentStep]
  );

  const handleBack = () => {
    setCurrentStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <div className="container">
      <div className="py-6 bg-white rounded-lg shadow mx-5 md:mx-8 lg:mx-0">
        <h3 className="text-center text-lg font-bold mb-2 text-gray-700">
          {eventData?.name}
        </h3>
        <h1
          className="text-2xl font-bold mb-6 text-center"
          style={{
            color: eventData?.font_color,
          }}
        >
          Please Enter your data
        </h1>
        <div className="flex justify-center">
          <form className="w-[90%] lg:w-3/4 p-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-5">
              <div className="md:col-span-2">
                <label
                  style={{ color: eventData?.font_color }}
                  className="text-lg font-medium"
                >
                  National ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="national_id"
                  value={values.national_id}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded mt-3"
                  placeholder="Enter national ID"
                />
                {errors.national_id && (
                  <p className="text-red-500">{errors.national_id}</p>
                )}
              </div>

              <div>
                <label
                  style={{ color: eventData?.font_color }}
                  className="text-lg font-medium"
                >
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={values.first_name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded mt-3"
                  placeholder="Enter first name"
                />
                {errors.first_name && (
                  <p className="text-red-500">{errors.first_name}</p>
                )}
              </div>

              <div>
                <label
                  style={{ color: eventData?.font_color }}
                  className="text-lg font-medium"
                >
                  Family Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="family_name"
                  value={values.family_name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded mt-3"
                  placeholder="Enter family name"
                />
                {errors.family_name && (
                  <p className="text-red-500">{errors.family_name}</p>
                )}
              </div>

              <div>
                <label
                  style={{ color: eventData?.font_color }}
                  className="text-lg font-medium"
                >
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded mt-3"
                  placeholder="Enter email"
                />
                {errors.email && <p className="text-red-500">{errors.email}</p>}
              </div>

              <div>
                <label
                  style={{ color: eventData?.font_color }}
                  className="text-lg font-medium"
                >
                  Phone Number{" "}
                  {values.organization?.toLowerCase() !== "abbott" && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <div className="flex mt-3">
                  <input
                    type="text"
                    value="+966"
                    disabled
                    className="w-16 p-2 border rounded-l bg-gray-100"
                  />
                  <input
                    name="phone_number"
                    onChange={handleChange}
                    value={values.phone_number}
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-r"
                    placeholder="Enter phone number"
                  />
                </div>
                {errors.phone_number && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.phone_number}
                  </p>
                )}
              </div>

              {/* <div>
                <label
                  style={{ color: eventData?.font_color }}
                  className="text-lg font-medium"
                >
                  City
                </label>
                <select
                  name="city_id"
                  value={values.city_id}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2.5 rounded mt-3"
                >
                  <option value="" hidden>
                    Select your city
                  </option>
                  {(values.doctor_master_data_id
                    ? currentCountryCities
                    : saudiArabiaCities
                  )?.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div> */}

              <div>
                <label
                  style={{ color: eventData?.font_color }}
                  className="text-lg font-medium"
                >
                  Specialty <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="specialty"
                  value={values.specialty}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded mt-3"
                  placeholder="Enter your specialty"
                />
                {errors.specialty && (
                  <p className="text-red-500">{errors.specialty}</p>
                )}
              </div>

              <div>
                <label
                  style={{ color: eventData?.font_color }}
                  className="text-lg font-medium"
                >
                  Medical Licence ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="medical_license"
                  value={values.medical_license}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded mt-3"
                  placeholder="Enter your licence ID"
                />
                {errors.medical_license && (
                  <p className="text-red-500">{errors.medical_license}</p>
                )}
              </div>

              <div>
                <label
                  style={{ color: eventData?.font_color }}
                  className="text-lg font-medium"
                >
                  Organization
                </label>
                <input
                  type="text"
                  name="organization"
                  value={values.organization}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded mt-3"
                  placeholder="Enter your organization"
                />
                {errors.organization && (
                  <p className="text-red-500">{errors.organization}</p>
                )}
              </div>

              <div>
                <label
                  style={{ color: eventData?.font_color }}
                  className="text-lg font-medium"
                >
                  Industry Area <span className="text-red-500">*</span>
                </label>
                <select
                  name="industry_area"
                  value={values.industry_area}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2.5 rounded mt-3"
                >
                  <option value="" hidden>
                    Select your industry area
                  </option>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
                {errors.industry_area && (
                  <p className="text-red-500">{errors.industry_area}</p>
                )}
              </div>
            </div>

            <div className="flex justify-between mt-10">
              <button
                className="border border-blueColor text-blueColor hover:bg-blueColor hover:text-white transition-all duration-300 px-6 py-2 lg:w-48 rounded-lg font-semibold"
                onClick={handleBack}
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  backgroundColor: eventData?.btn_color,
                  opacity: isSubmitting ? 0.7 : 1,
                }}
                className="text-white px-6 py-2 lg:w-48 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:cursor-not-allowed"
              >
                {isSubmitting && <i className="fas fa-spinner fa-spin"></i>}
                {isSubmitting ? "Processing..." : "Next"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RegisterForm;
